import { useState } from "react";
import { toast } from "sonner";
import { Printer, PackageCheck, Check, FileSearch, ThumbsUp, X } from "lucide-react";
import { api, errMsg, eur, dt, download } from "@/lib/api";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfrontoDialog } from "@/components/ConfrontoDialog";

const Badge = ({ cls, children }) => <span className={`rounded px-2 py-0.5 text-xs ${cls}`}>{children}</span>;

const Stato = ({ o }) => (
  <div className="flex flex-col items-start gap-1">
    {o.proposta ? <Badge cls="bg-violet-50 text-violet-700">Proposta da approvare</Badge>
      : o.caricato ? <Badge cls="bg-emerald-50 text-emerald-700">Ricevuto e caricato · {dt(o.data_ricevuto)}</Badge>
        : o.ricevuto ? <Badge cls="bg-sky-50 text-sky-700">Ricevuto · {dt(o.data_ricevuto)}</Badge>
          : <Badge cls="bg-amber-50 text-amber-700">In attesa</Badge>}
    {o.fattura && <Badge cls={o.fattura.conforme ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}>Fattura {o.fattura.numero}: {o.fattura.conforme ? "corrisponde" : "differenze"}</Badge>}
  </div>
);

const Azioni = ({ o, write, act, setCmp }) => (
  <>
    <Button variant="ghost" size="sm" data-testid={`order-print-${o.id}`} onClick={() => download(`/magazzino/ordini/${o.id}/pdf`).catch((e) => toast.error(e.message))}><Printer className="mr-1 h-4 w-4" />PDF</Button>
    {write && o.proposta && <>
      <Button variant="outline" size="sm" className="ml-1" data-testid={`order-approve-${o.id}`} onClick={() => act(() => api.post(`/magazzino/ordini/${o.id}/approva`), "Proposta approvata: ordine numerato")}><ThumbsUp className="mr-1 h-4 w-4" />Approva</Button>
      <Button variant="ghost" size="sm" data-testid={`order-discard-${o.id}`} onClick={() => act(() => api.delete(`/magazzino/ordini/${o.id}`), "Proposta scartata")}><X className="h-4 w-4" /></Button>
    </>}
    {write && !o.proposta && <Button variant="ghost" size="sm" data-testid={`order-invoice-${o.id}`} onClick={() => setCmp(o)}><FileSearch className="mr-1 h-4 w-4" />Fattura</Button>}
    {write && !o.proposta && !o.caricato && <>
      {!o.ricevuto && <Button variant="ghost" size="sm" data-testid={`order-received-${o.id}`} onClick={() => act(() => api.post(`/magazzino/ordini/${o.id}/ricevuto`, { carica: false }), `Ordine ${o.numero} segnato come ricevuto`)}><Check className="mr-1 h-4 w-4" />Ricevuto</Button>}
      <Button variant="outline" size="sm" className="ml-1" data-testid={`order-load-${o.id}`} onClick={() => act(() => api.post(`/magazzino/ordini/${o.id}/ricevuto`, { carica: true }), `Merce dell'ordine ${o.numero} caricata a magazzino`)}><PackageCheck className="mr-1 h-4 w-4" />Carica magazzino</Button>
    </>}
  </>
);

export const OrdiniList = ({ rows, write, onChange }) => {
  const [cmp, setCmp] = useState(null);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  if (rows.length === 0) return <Empty text="Nessun ordine al fornitore" />;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <Table data-testid="orders-table">
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Fornitore</TableHead><TableHead className="text-right">Articoli</TableHead>
          <TableHead className="text-right">Totale ind.</TableHead><TableHead>Stato</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((o) => (
            <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
              <TableCell className="font-mono text-sm font-semibold">{o.numero}</TableCell>
              <TableCell className="font-mono text-xs">{dt(o.data)}</TableCell>
              <TableCell className="text-sm">{o.fornitore?.nome}</TableCell>
              <TableCell className="text-right font-mono">{o.righe.length}</TableCell>
              <TableCell className="text-right font-mono">{eur(o.totale)}</TableCell>
              <TableCell data-testid={`order-status-${o.id}`}><Stato o={o} /></TableCell>
              <TableCell className="whitespace-nowrap text-right"><Azioni o={o} write={write} act={act} setCmp={setCmp} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfrontoDialog order={cmp} onClose={() => { setCmp(null); onChange(); }} />
    </div>
  );
};
